import React from 'react';
import { css, useTheme } from '@emotion/react';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import {
  type RestModalFinalOffer,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { FunnelButton } from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { useCardRenderEnvironment } from '@bbkFunnelComponents/card-render-environment';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import {
  ModalDesktopInnerSpacer,
  TitleTheme,
  useMediaQueries,
} from '../sections';

type Props = {
  modalInfo: RestModalFinalOffer;
  handleAction: (action: RestModalForwards) => void;
};

const RenderFinalOffer: React.FC<Props> = (props) => {
  const {
    modalInfo: {
      id,
      requires: {
        generic_actions = [],
        info: { title, details, image },
      },
    },
    handleAction,
  } = props;
  const { mobileView, tabletView, mobilePadding } = useMediaQueries();
  const theme = useTheme() as EmotionTheme;
  const equalButtonSize = theme.complianceEqualButtonSize;
  const { previewCanvas } = useCardRenderEnvironment();
  const isMobilePreview = previewCanvas?.device === 'mobile';

  return (
    <ModalDesktopInnerSpacer
      css={[
        mobilePadding,
        css`
          ${
            mobileView &&
            css`
              flex: 1 1 auto;
            `
          };
        `,
      ]}
      className={cs('final-offer', 'inner-spacer', 'display-grid', {
        desktop: tabletView,
        mobile: mobileView,
        hasImage: !!image,
      })}
    >
      <TitleTheme data-part="title" title={title} />

      <div css={{ marginBottom: '1rem' }} data-part="image">
        {image && (
          <img
            alt=""
            src={image}
            css={css`
              width: 100%;
              height: 100%;
              max-height: 600px;
              object-fit: cover;
              object-position: center;
              border-radius: 4px;
            `}
          />
        )}
      </div>

      <div data-part="description">
        <CustomerHtml value={details} />
      </div>
      {/* Added padding to make total 27px in mobile for equal size buttons */}
      <div
        className={cs('display-grid action-items', {
          '!tw-flex tw-flex-col tw-justify-center tw-px-[11px]':
            equalButtonSize && (mobileView || isMobilePreview),
          '!tw-flex tw-justify-center':
            equalButtonSize && !mobileView && !isMobilePreview,
        })}
      >
        {generic_actions.map((ga, idx) => (
          <FunnelButton
            data-part="cta"
            data-action-index={idx}
            transparent={idx > 0}
            key={idx}
            onClick={() => handleAction(ga)}
            classes="d-flex align-items-center justify-content-center"
          >
            {ga.icon && <ActionButtonIcons icon={ga.icon} />}
            <span style={{ paddingLeft: '5px' }}>{ga.text}</span>
          </FunnelButton>
        ))}
      </div>
    </ModalDesktopInnerSpacer>
  );
};

export const FinalOffer = Object.assign(RenderFinalOffer, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'right',
      options: {
        formatting: false,
      },
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    image: {
      path: ['requires', 'info', 'image'],
      control: 'image',
      selector: '[data-part="image"]',
      placement: 'top-end',
      options: {
        helpLabel: 'Recommended size: 1000x600px; < 300kb',
      },
    },
    actions: {
      path: ['requires', 'generic_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="cta"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
